import { NgRedux } from '@ptl/angular-redux-store';
import { EventEmitter, Injector, Input, OnDestroy, Output, QueryList } from '@angular/core';
import { Component, OnInit, ViewChildren } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { AppInjector, EntryConfigInterface } from '@ptl/forms';
import { StateStructure } from '@ptl/state';
import { FileDisplayComponent } from '../file-display/file-display.component';
import { DisplayableFile, ExistingDocs, FILE_UPLOAD_STATUS } from '../interfaces/displayable-file.interface';
import { EntitlementsHelperService, LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';


@Component({
    selector: 'app-required-docs-upload',
    templateUrl: './required-docs-upload.component.html',
    styleUrls: ['./required-docs-upload.component.scss'],
    standalone: false
})
export class RequiredDocsUploadComponent implements OnInit, OnDestroy {

  @Output() postUploadAllBehavior: EventEmitter<any> = new EventEmitter();
  
  @Input() postFileInputBehavior: () => void;
  @Input() uploadFile: (fileComponent: FileDisplayComponent) => void;
  @Input() removeFile: (fileComponent: FileDisplayComponent) => void;

  @Input() customConfigs: {
    formNameValue: string; // Passed along with payload to indicate type of document ( backend uses to organize files )
    allowedExtensions?: string[];
    minDocuments?: number;
    maxDocuments?: number;
    fifthBullet?: string;
    disableRemoveButtonOnSuccess?: boolean;
  };

  @Input() entry: EntryConfigInterface;

  @Input() existingDocs: ExistingDocs[];
  @Input() allowedFileExtensions: string[];
  
  @ViewChildren('FileDisplayComponent') fileDisplayRows: QueryList<FileDisplayComponent>;

  fileSizeLimitInMB = 7;
  fileSizeLimitInBytes = this.fileSizeLimitInMB * 1000000;

  // maxUploadSizeInMB = 35;
  // maxUploadSizeInBytes = this.maxUploadSizeInMB * 1000000;

  maxUploadFileNumber = 5;

  canAddMoreFiles: boolean;
  canUploadMoreFiles: boolean;

  fileListArray: File[];
  fileDisplayArray: DisplayableFile[];

  displayedColumns: string[] = ['name', 'size', 'status', 'remove'];

  allowedExtensions = [
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.txt',
    '.rtf',
    '.png',
    '.jpg',
    '.bmp',
    '.ppt',
    '.pptx',
    '.pdf'
  ];

  allowedExtensionsAsString: string;

  private injector: Injector;
  protected ngRedux: NgRedux<StateStructure>;
  protected entitlementsHelper: EntitlementsHelperService;
  protected fb: UntypedFormBuilder;
  protected reducerUtilityService: ReducerUtilityService;

  constructor( ) {
    this.injector = AppInjector.getInjector();
    this.ngRedux = this.injector.get(NgRedux);
    this.entitlementsHelper = this.injector.get(EntitlementsHelperService)
    this.fb = this.injector.get(UntypedFormBuilder);
    this.reducerUtilityService = this.injector.get(ReducerUtilityService);
  }

  ngOnInit() {
    this.maxUploadFileNumber = this.customConfigs.maxDocuments ? this.customConfigs.maxDocuments : this.maxUploadFileNumber;

    /**
     * this.allowedFileExtensions - was here originally, may or may not be used, is 3am 
     * this.customConfigs.allowedExtensions - I'm adding to support a trimming of MN's file types on enrollment-side document-upload
     **/
    const allowedFileExtensionsSrc = this.allowedFileExtensions || this.customConfigs.allowedExtensions;
    this.allowedExtensionsAsString = allowedFileExtensionsSrc ? allowedFileExtensionsSrc.join(',') : this.allowedExtensions.join(',');

    /** Pre-populated existing documents if available */
    if (this.existingDocs) {

      this.fileDisplayArray = this.existingDocs.map(d => d.displayableFileObj);
      this.fileListArray = new Array(this.existingDocs.length); // placeholder, we don't actually have file data, but, don't need it for previously uploaded docs

      this.validateAddMoreAndUploadMoreFiles();
    }
  }

  ngOnDestroy() {
  }

  trackByIndexNameSize(index: number, file: DisplayableFile): string {
    return index + file.name + file.size;
  }

  onHandleFileInput(event: Event, files: FileList) {
    /**
     * Here, we will construct 2 arrays:
     * 1) fileListArray - has the actual file data
     * 2) fileDisplayArray - will be used for display purposes and UI checks only
     */

    // data array
    if (!this.fileListArray) {
      this.fileListArray = [];
    }

    this.fileListArray.push(files.item(0));

    // display array
    if (!this.fileDisplayArray) {
      this.fileDisplayArray = [];
    }

    const convertedFile: DisplayableFile = {
      name: files.item(0).name,
      size: files.item(0).size,
      status: FILE_UPLOAD_STATUS.NEW,
      documentRecord: null
    };
    this.fileDisplayArray.push(convertedFile);

    const selectedFileInput: HTMLInputElement = event.target as HTMLInputElement;

    /** Clear this so that if we remove/re-add the same file, this event will re-trigger */
    selectedFileInput.value = null;

    this.extraFileInputBehavior();
    this.validateAddMoreAndUploadMoreFiles();

    if (this.postFileInputBehavior) {
      this.postFileInputBehavior();
    }
  }

  onUploadFile() {
    return (fileComponent: FileDisplayComponent) => {
      if (this.uploadFile) {
        this.uploadFile(fileComponent);
      }
    };
  }

  onRemoveFile() {
    return (fileComponent: FileDisplayComponent): void => {

      /** Remove the file from the arrays */
      this.fileListArray.splice(fileComponent.index, 1);
      this.fileDisplayArray.splice(fileComponent.index, 1);

      if (this.removeFile) {
        this.removeFile(fileComponent);
      }

      this.extraFileRemovalBehavior(fileComponent);
      this.validateAddMoreAndUploadMoreFiles();
    }
  }

  /**
   * Loop through each component, checking whether it's pending upload
   * or already successfully uploaded - and if not - upload!
   */
  uploadAllFiles() {
    this.fileDisplayRows.forEach(fileDisplayComponent => {
      const safeForUpload = (() => {
        if (fileDisplayComponent.isPending) { return false; }
        if (fileDisplayComponent.successfullySubmitted) { return false; }
        if (!fileDisplayComponent.validForUpload) { return false; }
        return true;
      })();

      if (safeForUpload) {
        fileDisplayComponent.uploadSingleFile();
      }
    });
    this.postUploadAllBehavior.emit();
    /** NEED LOGIC FOR WHEN FAILURES OCCUR? OR JUST SAY WHATEVER? */
  }

  /**
   * Won't remove files which have already been uploaded successfully 
   */
  removeAllFiles() {
    const totalFiles = this.fileDisplayRows.length;
    const queryListAsArray = this.fileDisplayRows.toArray();

    // Remove ALL files - whether uploaded or not
    for (let i = totalFiles - 1; i >= 0; i--) {
      queryListAsArray[i].removeSingleFile();
    }

    this.validateAddMoreAndUploadMoreFiles();
  }

  private getTotalFilesSize(): number {
    const fileSizeArray = (() => {
      const array = [];
      this.fileDisplayArray.forEach(file => {
        array.push(file.size);
      });
      return array;
    })();

    let totalFilesSize = 0;
    if (fileSizeArray.length) {
      totalFilesSize = fileSizeArray.reduce((a, b) => a + b);
    }

    return totalFilesSize;
  }

  /** Loop through components, grab their file's size if they HAVE been uploaded OR are pending, and return */
  private getTotalUploadedFilesSize(): number {
    const previouslyUploadedFiles = this.fileDisplayArray.filter(r => {
      return (r.status === FILE_UPLOAD_STATUS.SUCCESS || r.status === FILE_UPLOAD_STATUS.PENDING);
    });
    const previousUploadedValueTotal = (() => {
      const array = [];

      previouslyUploadedFiles.forEach(r => {
        array.push(r.size);
      });

      let totalUploadedFilesSize = 0;
      if (array.length) {
        totalUploadedFilesSize = array.reduce((a, b) => a + b);
      }

      return totalUploadedFilesSize;
    })();

    return previousUploadedValueTotal;
  }

  /** Loop through components, grab their file's size if they HAVE NOT been uploaded, and return */
  private getTotalUnUploadedFilesSize(): number {
    const yetToBeUploadedFiles = this.fileDisplayArray.filter(r => {
      return !(r.status === FILE_UPLOAD_STATUS.SUCCESS || r.status === FILE_UPLOAD_STATUS.PENDING);
    });
    const yetToBeUploadedFilesValueTotal = (() => {
      const array = [];
      yetToBeUploadedFiles.forEach(r => {
        array.push(r.size);
      });

      let totalUploadedFilesSize = 0;
      if (array.length) {
        totalUploadedFilesSize = array.reduce((a, b) => a + b);
      }

      return totalUploadedFilesSize;
    })();

    return yetToBeUploadedFilesValueTotal;
  }

  protected validateAddMoreAndUploadMoreFiles() {
    this.validateAddMoreFiles();
    this.validateUploadMoreFiles();
  }

  /** Only call validateAddMoreAndUploadMoreFiles() */
  private validateAddMoreFiles() {
    if (this.fileDisplayArray?.length >= this.maxUploadFileNumber) {
      this.canAddMoreFiles = false;
      // } else if (this.getTotalFilesSize() >= this.maxUploadSizeInBytes) {
      //   this.canAddMoreFiles = false;
    } else {
      this.canAddMoreFiles = true;
    }
  }

  /** Only call validateAddMoreAndUploadMoreFiles() */
  private validateUploadMoreFiles() {

    /** File Size - has been breached */
    // if (this.getTotalUploadedFilesSize() + this.getTotalUnUploadedFilesSize() > this.maxUploadSizeInBytes) {
    //   this.canUploadMoreFiles = false;
    // }

    /** Can upload */
    // else {
    this.canUploadMoreFiles = true;
    // }

  }

  addPayloadAdditions(): (formData: FormData, displayableFileObj: DisplayableFile) => void {
    return (formData: FormData, displayableFileObj: DisplayableFile) => {
      if (this.customConfigs.formNameValue) {
        formData.append(
          'formname',
          this.customConfigs.formNameValue
        );
      }
    }
  }

  extraFileInputBehavior() { /** placeholder */ }
  extraFileRemovalBehavior(fileComponent: FileDisplayComponent) { /** placeholder */ }

}
